import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-aria-components/Button';
import { countries } from '../../shared/data/countries';
import { COUNTRY_TIME_ZONES } from '../../shared/data/country-timezones';
import { useAnnouncer } from '../../shared/accessibility/Announcer';
import { locale } from '../../services/appwrite/client';
import { useDocumentTitle } from '../../shared/document-title';
import { availableTimeZones, timeZoneOffsetLabel } from './timezones';
import { createOwnProfile, type Disability, type Gender } from './registration-gateway';
import { useSession } from './SessionContext';

const MINIMUM_AGE = 18;

const MONTH_OPTIONS = [
  { value: '1', label: 'Enero' }, { value: '2', label: 'Febrero' }, { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' }, { value: '5', label: 'Mayo' }, { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' }, { value: '8', label: 'Agosto' }, { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' }, { value: '11', label: 'Noviembre' }, { value: '12', label: 'Diciembre' },
];

// Las condiciones van primero y las respuestas de exclusión ("Ninguna",
// "Prefiero no decir") al final, para no dar a entender que "ninguna" es lo
// esperable. "Salud mental" y "Enfermedad crónica" se sacaron del alta: son
// datos sensibles sin un uso concreto todavía — se piden solo si en algún
// momento producen una adaptación real.
const DISABILITY_OPTIONS: Array<{ value: Disability; label: string }> = [
  { value: 'blindness', label: 'Ceguera' },
  { value: 'lowVision', label: 'Baja visión' },
  { value: 'hearing', label: 'Auditiva' },
  { value: 'physical', label: 'Física o motriz' },
  { value: 'cognitive', label: 'Cognitiva' },
  { value: 'neurodivergent', label: 'Neurodivergencia' },
  { value: 'speech', label: 'Del habla' },
  { value: 'another', label: 'Otra discapacidad' },
  { value: 'none', label: 'Ninguna' },
  { value: 'preferNotToSay', label: 'Prefiero no decir' },
];

/** Estas dos vacían cualquier otra elección, y se vacían si se elige otra cosa. */
const EXCLUSIVE_DISABILITY: Disability[] = ['none', 'preferNotToSay'];

function ageAt(birthDate: Date, moment: Date): number {
  let age = moment.getFullYear() - birthDate.getFullYear();
  const beforeBirthday =
    moment.getMonth() < birthDate.getMonth() ||
    (moment.getMonth() === birthDate.getMonth() && moment.getDate() < birthDate.getDate());
  return beforeBirthday ? age - 1 : age;
}

/** El identificador IANA es un dato técnico ("America/Argentina/Buenos_Aires");
 * esto muestra solo la última parte, más legible ("Buenos Aires"). */
function friendlyZoneLabel(zone: string): string {
  const city = zone.split('/').pop();
  return city ? city.replace(/_/g, ' ') : zone;
}

/** Respaldo cuando falla la detección por geoip: el país que sugiere la
 * configuración regional del navegador (por ejemplo, "es-AR" → "AR"). */
function countryCodeFromLocale(): string {
  const localeName = Intl.DateTimeFormat().resolvedOptions().locale || navigator.language || '';
  const parts = localeName.split('-');
  const last = parts.length > 1 ? parts[parts.length - 1] : undefined;
  return last ? last.toUpperCase() : '';
}

/**
 * Formulario de registro: se completa una sola vez, después del primer login.
 *
 * Se muestra en lugar de toda la aplicación —sin cabecera ni menú— mientras
 * la cuenta no tenga fila de perfil. `App.tsx` decide cuándo mostrarlo; este
 * componente solo sabe pedir los datos y guardarlos.
 */
export function RegistrationGate() {
  const { state, refresh, signOut } = useSession();
  const { announce } = useAnnouncer();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const birthDayRef = useRef<HTMLSelectElement>(null);
  const birthMonthRef = useRef<HTMLSelectElement>(null);
  const birthYearRef = useRef<HTMLSelectElement>(null);
  const today = new Date();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const maxBirthYear = today.getFullYear() - MINIMUM_AGE;
  const [birthDay, setBirthDay] = useState(String(today.getDate()));
  const [birthMonth, setBirthMonth] = useState(String(today.getMonth() + 1));
  const [birthYear, setBirthYear] = useState(String(maxBirthYear));
  const [countryCode, setCountryCode] = useState('');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [showTimezonePicker, setShowTimezonePicker] = useState(false);
  const [disabilities, setDisabilities] = useState<Disability[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const email = state.status === 'authenticated' ? state.profile.user.email : '';
  const userId = state.status === 'authenticated' ? state.profile.user.id : '';

  useDocumentTitle('Completa tu perfil');

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    if (state.status !== 'authenticated') return;
    const nameParts = state.profile.user.name.trim().split(/\s+/).filter(Boolean);
    const [detectedFirstName, ...restOfName] = nameParts;
    if (detectedFirstName) {
      setFirstName((current) => current || detectedFirstName);
      setLastName((current) => current || restOfName.join(' '));
    }

    let cancelled = false;
    (async () => {
      let detected = '';
      try {
        const result = await locale.get();
        detected = result.countryCode || '';
      } catch {
        // Sin geoip disponible, se prueba con la configuración regional del navegador.
      }
      if (!detected || !countries.some((country) => country.code === detected)) {
        const fallback = countryCodeFromLocale();
        detected = countries.some((country) => country.code === fallback) ? fallback : '';
      }
      if (!cancelled && detected) setCountryCode((current) => current || detected);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  const leave = async () => {
    setSigningOut(true);
    try {
      await signOut();
      announce('Sesión cerrada.');
    } catch (error) {
      console.error('[Club Accesible] No se pudo cerrar la sesión.', error);
      announce('No pudimos cerrar la sesión. Inténtalo de nuevo.');
      setSigningOut(false);
    }
  };

  const toggleDisability = (value: Disability) => {
    setDisabilities((current) => {
      const selected = current.includes(value);
      if (selected) return current.filter((item) => item !== value);
      if (EXCLUSIVE_DISABILITY.includes(value)) return [value];
      return [...current.filter((item) => !EXCLUSIVE_DISABILITY.includes(item)), value];
    });
  };

  /** Flecha derecha/izquierda mueve el foco entre Día/Mes/Año, como los tres
   * segmentos de un `<input type="date">` nativo — arriba/abajo siguen
   * cambiando el valor del propio `<select>`, sin tocarlos. */
  const birthFieldRefs = { day: birthDayRef, month: birthMonthRef, year: birthYearRef };
  const birthFieldOrder: Array<keyof typeof birthFieldRefs> = ['day', 'month', 'year'];
  const handleBirthFieldKeyDown = (current: keyof typeof birthFieldRefs) => (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const index = birthFieldOrder.indexOf(current);
    const step = event.key === 'ArrowRight' ? 1 : -1;
    // Da la vuelta en los extremos (Año + derecha → Día, Día + izquierda →
    // Año) en vez de no hacer nada: así siempre pasa algo con la flecha.
    const targetIndex = (index + step + birthFieldOrder.length) % birthFieldOrder.length;
    const target = birthFieldOrder[targetIndex];
    if (!target) return;
    event.preventDefault();
    birthFieldRefs[target].current?.focus();
  };

  const dayOptions = Array.from({ length: 31 }, (_, index) => String(index + 1));
  // Arranca en el año más reciente compatible con la edad mínima: el resto
  // (2019, 2020...) nunca podría pasar la validación, así que no tiene
  // sentido ofrecerlos — día y mes siguen decidiendo si ya cumplió, dentro
  // de ese año.
  const yearOptions = Array.from({ length: maxBirthYear - 1900 + 1 }, (_, index) => String(maxBirthYear - index));
  const timeZones = availableTimeZones();

  const parsedBirthDate = (): Date => new Date(Number(birthYear), Number(birthMonth) - 1, Number(birthDay));

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!firstName.trim()) next.firstName = 'Escribe tu nombre.';
    if (!lastName.trim()) next.lastName = 'Escribe tu apellido.';
    const parsed = parsedBirthDate();
    const isValidCalendarDate =
      parsed.getFullYear() === Number(birthYear) && parsed.getMonth() === Number(birthMonth) - 1 && parsed.getDate() === Number(birthDay);
    if (!isValidCalendarDate) next.birthDate = 'La fecha no es válida.';
    else if (ageAt(parsed, today) < MINIMUM_AGE) next.birthDate = 'Debes tener 18 años o más para crear una cuenta.';
    if (!countryCode) next.countryCode = 'Elige tu país.';
    if (!disabilities.length) next.disabilities = 'Elige al menos una opción.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      await createOwnProfile(userId, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        preferredName: firstName.trim(),
        birthDate: parsedBirthDate(),
        countryCode,
        timezone,
        // El género se pide desde el perfil, no en este alta — se guarda un
        // valor neutro porque la columna es obligatoria en la base de datos.
        gender: 'preferNotToSay' as Gender,
        disabilities,
        accessibilityNotes: '',
      });
      await refresh();
    } catch (error) {
      console.error('[Club Accesible] No se pudo crear el perfil.', error);
      setSubmitError('No pudimos guardar tu registro. Revisa tu conexión e inténtalo de nuevo.');
      setSubmitting(false);
    }
  };

  return (
    <main id="main-content" className="container site-main" tabIndex={-1}>
      <section className="panel-card">
        <h1 ref={headingRef} tabIndex={-1}>Completa tu perfil</h1>
        <p className="lead">
          Necesitamos algunos datos para terminar de crear tu cuenta. Después podrás revisarlos o
          modificarlos desde tu perfil.
        </p>
        <p className="mb-4">
          <Button className="btn btn-outline-secondary" type="button" isDisabled={signingOut} onPress={() => void leave()}>
            {signingOut ? 'Cerrando sesión…' : 'Cerrar sesión'}
          </Button>
        </p>

        <form noValidate onSubmit={onSubmit}>
          <fieldset className="mb-4">
            <legend className="h5">Datos personales</legend>

            <div className="mb-3">
              <label className="form-label" htmlFor="reg-first-name">Nombre</label>
              <input className="form-control" id="reg-first-name" type="text" autoComplete="given-name" value={firstName} onChange={(event) => setFirstName(event.target.value)} aria-invalid={errors.firstName ? true : undefined} />
              {errors.firstName ? <p className="error-message" role="alert">{errors.firstName}</p> : null}
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="reg-last-name">Apellido</label>
              <input className="form-control" id="reg-last-name" type="text" autoComplete="family-name" value={lastName} onChange={(event) => setLastName(event.target.value)} aria-invalid={errors.lastName ? true : undefined} />
              {errors.lastName ? <p className="error-message" role="alert">{errors.lastName}</p> : null}
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="reg-email">Correo electrónico</label>
              <input className="form-control" id="reg-email" type="email" value={email} readOnly disabled aria-describedby="reg-email-help" />
              <p id="reg-email-help" className="form-text">Este correo está asociado a tu cuenta.</p>
            </div>

            <fieldset className="nested-fieldset mb-3">
              <legend className="h6">Fecha de nacimiento</legend>
              <p className="form-text mt-0">Debes tener 18 años o más para registrarte.</p>
              <div className="row g-2">
                <div className="col-12 col-sm-3">
                  <label className="form-label" htmlFor="reg-birth-day">Día</label>
                  <select
                    ref={birthDayRef}
                    className="form-select"
                    id="reg-birth-day"
                    value={birthDay}
                    onChange={(event) => setBirthDay(event.target.value)}
                    onKeyDown={handleBirthFieldKeyDown('day')}
                    aria-invalid={errors.birthDate ? true : undefined}
                  >
                    {dayOptions.map((day) => <option key={day} value={day}>{day}</option>)}
                  </select>
                </div>
                <div className="col-12 col-sm-5">
                  <label className="form-label" htmlFor="reg-birth-month">Mes</label>
                  <select
                    ref={birthMonthRef}
                    className="form-select"
                    id="reg-birth-month"
                    value={birthMonth}
                    onChange={(event) => setBirthMonth(event.target.value)}
                    onKeyDown={handleBirthFieldKeyDown('month')}
                  >
                    {MONTH_OPTIONS.map((month) => <option key={month.value} value={month.value}>{month.label}</option>)}
                  </select>
                </div>
                <div className="col-12 col-sm-4">
                  <label className="form-label" htmlFor="reg-birth-year">Año</label>
                  <select
                    ref={birthYearRef}
                    className="form-select"
                    id="reg-birth-year"
                    value={birthYear}
                    onChange={(event) => setBirthYear(event.target.value)}
                    onKeyDown={handleBirthFieldKeyDown('year')}
                  >
                    {yearOptions.map((year) => <option key={year} value={year}>{year}</option>)}
                  </select>
                </div>
              </div>
              {errors.birthDate ? <p className="error-message" role="alert">{errors.birthDate}</p> : null}
            </fieldset>
          </fieldset>

          <fieldset className="mb-4">
            <legend className="h5">Ubicación</legend>

            <div className="mb-3">
              <label className="form-label" htmlFor="reg-country">País</label>
              <select
                className="form-select"
                id="reg-country"
                value={countryCode}
                onChange={(event) => {
                  const nextCountry = event.target.value;
                  setCountryCode(nextCountry);
                  const suggestedTimeZone = COUNTRY_TIME_ZONES[nextCountry];
                  if (suggestedTimeZone) setTimezone(suggestedTimeZone);
                }}
                aria-invalid={errors.countryCode ? true : undefined}
              >
                <option value="">Selecciona tu país</option>
                {countries.map((country) => <option key={country.code} value={country.code}>{country.name}</option>)}
              </select>
              {errors.countryCode ? <p className="error-message" role="alert">{errors.countryCode}</p> : null}
            </div>

            <div className="mb-3">
              <span className="form-label d-block">Zona horaria</span>
              {!showTimezonePicker ? (
                <>
                  <p className="mb-1">{friendlyZoneLabel(timezone)} ({timeZoneOffsetLabel(timezone)})</p>
                  <p className="form-text">Usaremos esta zona horaria para mostrar las actividades en tu horario local.</p>
                  <Button
                    className="btn btn-link p-0"
                    type="button"
                    onPress={() => setShowTimezonePicker(true)}
                    aria-label={`Zona horaria: ${friendlyZoneLabel(timezone)} (${timeZoneOffsetLabel(timezone)}). Cambiar zona horaria.`}
                  >
                    Cambiar zona horaria
                  </Button>
                </>
              ) : (
                <>
                  <label className="form-label" htmlFor="reg-timezone">Selecciona tu zona horaria</label>
                  <select className="form-select mb-2" id="reg-timezone" value={timezone} onChange={(event) => setTimezone(event.target.value)}>
                    {timeZones.map((zone) => <option key={zone} value={zone}>{`${friendlyZoneLabel(zone)} (${timeZoneOffsetLabel(zone)})`}</option>)}
                  </select>
                  <Button className="btn btn-link p-0" type="button" onPress={() => setShowTimezonePicker(false)}>
                    Usar esta zona horaria
                  </Button>
                </>
              )}
            </div>
          </fieldset>

          <fieldset className="mb-4 nested-fieldset" id="disability-group">
            <legend className="h5">Discapacidad (opcional)</legend>
            <p className="form-text mt-0">
              Puedes seleccionar una o más opciones. Esta información es opcional y privada: la
              usamos para conocer mejor las necesidades de accesibilidad de nuestra comunidad. No
              se muestra públicamente en tu perfil.
            </p>
            {DISABILITY_OPTIONS.map((option) => (
              <div className="form-check" key={option.value}>
                <input className="form-check-input" type="checkbox" id={`reg-disability-${option.value}`} checked={disabilities.includes(option.value)} onChange={() => toggleDisability(option.value)} />
                <label className="form-check-label" htmlFor={`reg-disability-${option.value}`}>{option.label}</label>
              </div>
            ))}
            {errors.disabilities ? <p className="error-message" role="alert">{errors.disabilities}</p> : null}
          </fieldset>

          <Button className="btn btn-accent" type="submit" isPending={submitting}>Completar registro</Button>
          {submitError ? <p className="error-message" role="alert">{submitError}</p> : null}
        </form>
      </section>
    </main>
  );
}
